import './index.scss';
import React from 'react';
import debounce from 'lodash/debounce';
import NumberFormat from 'react-number-format';
import { connect } from 'react-redux';
import { isSameDay } from 'date-fns';
import { Form, Layout, Button, Row, Col, Input, AutoComplete, Alert } from 'antd';
import { DeleteOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { BookingStep } from '../../constants';
import { User, createUser } from '../../types/user';
import { bookingActions } from '../../actions/booking';
import { getUserFullName } from '../../utils/bookingUtil';
import { BookingItem } from '../../types/booking';

interface Props {
  users: User[];
  suggestedUsers: any;
  updateStep: (step: number) => void;
  storeActiveUser: (userId: number) => void;

  newUserFlag: number;
  storeNewUserFlag: (userId: number) => void;

  updateUsersList: (users: User[]) => void;
  updateSuggestedUsers: (suggestedUsers: any) => void;
  fetchUsersByPhone: (options: any) => Promise<User[]>;

  bookingHistory: Record<string, BookingItem[]>;
  storeBookingHistory: (options: Record<string, BookingItem[]>) => void;
  fetchBookingHistory: (salonId: number, phoneNumber: string) => Promise<BookingItem[]>;

  salonId: number;
  isAdmin: boolean;
  accessToken: string;
  primaryColor: string;
}

interface States {
  users: User[];
  newUserFlag: number;
}

const maxClients = 4;

class BookingUsers extends React.PureComponent<Props, States> {
  formRef;

  constructor(props) {
    super(props);
    this.formRef = React.createRef();
    this.state = {
      users: props.users,
      newUserFlag: props.newUserFlag || (props.users.length + 1),
    };
  }

  private formatPhoneNumber(phone: string) {
    return String(phone)
      .replace('(', '')
      .replace(')', '')
      .replace('-', '')
      .replace(' ', '')
      .trim();
  }

  private redirect(url: string) {
    const targetWindow = window.parent || window;
    if (targetWindow) {
      targetWindow.location.href = url;
    }
  }

  componentDidMount() {
    const { users } = this.props;
    this.setFieldsValue(users);
  }

  onSubmit = ({ users: submittedUsers }) => {
    const { users, newUserFlag } = this.state;
    const { updateStep, updateUsersList, storeNewUserFlag } = this.props;
    const bookingUsers = users
      .map((user, index) => {
        return { ...user, ...(submittedUsers[index] || {}) };
      })
      .filter(user => !!user.firstName)
      ;

    storeNewUserFlag(newUserFlag);
    updateUsersList(bookingUsers);
    updateStep(BookingStep.BookingDetail);
  }

  addUser = () => {
    const { users, newUserFlag } = this.state;
    const newUser = createUser({
      user_id: newUserFlag,
      can_delete: true,
      can_select_service: true,
      can_select_stylist: true,
      can_select_date_time: false,
    });

    this.setState({
      users: [...users, newUser],
      newUserFlag: newUserFlag + 1,
    });
  }

  deleteUser = (id: number) => {
    const { users } = this.state;
    const filteredUsers = [...users].filter((item: any) => item.id !== id);

    this.setState({ users: filteredUsers });
    this.setFieldsValue(filteredUsers);
  }

  getSuggestedUsers = (userId: number) => {
    const { suggestedUsers } = this.props;
    return (suggestedUsers[userId] || [])
      .map(user => {
        return {
          value: user.phoneNumber,
          label: (
            <div className="booking-user-option">
              <div>{getUserFullName(user)}</div>
              <div style={{ fontSize:12, opacity: 0.65 }}>{user.phoneNumber}</div>
            </div>
          ),
        };
      });
  }

  fetchBookingHistory = debounce((phone: string) => {
    const {
      salonId,
      bookingHistory,
      fetchBookingHistory,
      storeBookingHistory,
    } = this.props;

    const phoneNumber = this.formatPhoneNumber(phone);
    if (phoneNumber.length >= 10) {
      try {
        fetchBookingHistory(salonId, phoneNumber)
          .then(bookings => {
            storeBookingHistory({
              ...bookingHistory,
              [phone]: bookings,
            });
          });
      } catch (error) {}
    }
  }, 300);

  onChangeUser = (userId: number, fieldName: string, fieldValue: string) => {
    const { users } = this.state;
    const newUsers = [...users].map((user: User) => {
      if (user.id === userId) {
        return {
          ...user,
          [fieldName]: fieldValue,
        };
      }

      return user;
    });

    this.setState({ users: newUsers });
  }

  onSelectUser = (userId: number, phone) => {
    const { users } = this.state;
    const { suggestedUsers } = this.props;
    const {
      profileId,
      firstName,
      lastName,
      phoneNumber,
    } = (suggestedUsers[userId] || []).find((user: User) => user.phoneNumber === phone) || {} as any;
    const newUsers = [...users].map((user: User) => {
      return user.id === userId ? { ...user, firstName, lastName, phoneNumber, profileId, canViewProfile: true } : user;
    });

    this.setState({ users: newUsers });
    this.setFieldsValue(newUsers);
    this.fetchBookingHistory(phoneNumber);
  }

  getInput = (props, type = 'text') => {
    const { userId, ...nestedProps } = props;
    const { isAdmin } = this.props;

    if (type === 'phone') {
      if (isAdmin) {
        return (
          <AutoComplete
            onFocus={() => {}}
            options={this.getSuggestedUsers(userId)}
            onSelect={phoneNumber => this.onSelectUser(userId, phoneNumber)}
          >
            <div>
              <NumberFormat
                {...nestedProps}
                customInput={Input}
                format="(###) ###-####"
                type="tel"
              />
            </div>
          </AutoComplete>
        );
      }

      return (
        <NumberFormat
          {...nestedProps}
          customInput={Input}
          format="(###) ###-####"
          type="tel"
        />
      );
    }

    return <Input {...nestedProps} />;
  }

  searchUsersByPhone = debounce((userId: number, phoneNumber: any) => {
    const {
      salonId,
      accessToken,
      suggestedUsers,
      fetchUsersByPhone,
      updateSuggestedUsers,
    } = this.props;
    const phone = String(phoneNumber)
      .replace('(', '')
      .replace(')', '')
      .replace('-', '')
      .replace(' ', '')
      .trim();

    if (phone.length > 3) {
      try {
        const fetchOptions = {
          salonId,
          accessToken,
          phoneNumber: phone,
        };
        fetchUsersByPhone(fetchOptions)
          .then(matchedUsers => {
            const suggestedUsersList = {};
            suggestedUsersList[userId] = matchedUsers;

            updateSuggestedUsers(Object.assign({}, suggestedUsers, suggestedUsersList));
          });
      } catch (error) {}
    }
  }, 300);

  setFieldsValue = (users: User[]) => {
    const userForm = this.formRef.current;
    if (userForm) {
      userForm.setFieldsValue({ users });
    }
  }

  renderUserRow = (user: User, index: number) => {
    const { isAdmin, bookingHistory, primaryColor } = this.props;
    const userBookingHistory = bookingHistory[user.phoneNumber] || [];
    const todayBooking = userBookingHistory.find(booking => isSameDay(booking.date, new Date()));

    return (
      <div className="booking-user" key={`user-${user.id}`}>
        <Row className="booking-user-row" gutter={24}>
          {isAdmin && (
            <Col xs={2} sm={1}>
              {index + 1}.
            </Col>
          )}

          <Col xs={18} sm={19} lg={16} xl={12}>
            <Row gutter={24} style={{ marginBottom: -10 }}>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['users', index, 'phoneNumber']}
                  rules={[{
                    required: !user.canDelete,
                    message: 'This field is required!'
                  }]}
                  style={{ marginBottom: 10 }}
                >
                  {this.getInput(
                    {
                      userId: user.id,
                      value: user.phoneNumber,
                      placeholder: !user.canDelete ? 'Phone number' : 'Optional phone number',
                      onChange: (evt) => {
                        const inputedPhone = evt.target.value.trim();

                        if (isAdmin) {
                          this.searchUsersByPhone(user.id, inputedPhone);
                        }

                        this.onChangeUser(user.id, 'phoneNumber', inputedPhone);
                        this.fetchBookingHistory(inputedPhone);
                      },
                    },
                    'phone',
                  )}
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  name={['users', index, 'firstName']}
                  rules={[{
                    required: true,
                    message: 'This field is required!'
                  }]}
                  style={{ marginBottom: 10 }}
                  valuePropName="value"
                >
                  {this.getInput(
                    {
                      placeholder: 'First name',
                      value: user.firstName,
                      userId: user.id,
                      onChange: (evt) => {
                        const firstName = evt.target.value.trim();
                        this.onChangeUser(user.id, 'firstName', firstName);
                      },
                    }
                  )}
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  name={['users', index, 'lastName']}
                  rules={[{
                    required: !user.canDelete,
                    message: 'This field is required!'
                  }]}
                  style={{ marginBottom: 10 }}
                  valuePropName="value"
                >
                  {this.getInput(
                    {
                      placeholder: !user.canDelete ? 'Last name' : 'Optional last name',
                      value: user.lastName,
                      userId: user.id,
                      onChange: (evt) => {
                        const lastName = evt.target.value.trim();
                        this.onChangeUser(user.id, 'lastName', lastName);
                      },
                    }
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={3} sm={3}>
            {user.canDelete && (
              <Button
                danger
                type="link"
                icon={<DeleteOutlined />}
                style={{
                  position: "relative",
                  fontSize: 20,
                  top: -2,
                }}
                onClick={() => this.deleteUser(user.id)}
              />
            )}
          </Col>

          {todayBooking && (
            <Col
              xs={{ span: 22, offset: 2 }}
              sm={{ span: 23, offset: 1 }}
              style={{ marginBottom: -6, marginTop: 16 }}
            >
              There is an existing booking for this user today at <span
                style={{ color: primaryColor, fontWeight: 600 }}
              >{todayBooking.time}</span>. {isAdmin && todayBooking.link && (
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => this.redirect(todayBooking.link)}
                >View it?</span>
              )}
            </Col>
          )}
        </Row>
      </div>
    );
  }

  render() {
    const { users } = this.state;
    const { isAdmin } = this.props;

    return (
      <Form
        ref={this.formRef}
        initialValues={{}}
        onFinish={(values: any) => this.onSubmit(values)}
      >
        <Layout className="fave-layout booking-users">
          <Layout.Content className="fave-layout--content" style={{ paddingTop: 30 }}>
            {isAdmin && (
              <div style={{ marginBottom: 20 }}>
                <Alert
                  type="warning"
                  style={{ marginBottom: 16 }}
                  message={`Max ${maxClients} clients per booking`}
                />
              </div>
            )}

            <div className="booking-users--heading">
              <Row align="middle" gutter={24}>
                {isAdmin && <Col xs={2} sm={1}>#</Col>}
                <Col xs={18} sm={19} lg={16} xl={12}>
                  <Row gutter={24}>
                    <Col xs={24} sm={8}>Phone Number</Col>
                    <Col xs={24} sm={8}>First Name</Col>
                    <Col xs={24} sm={8}>Last Name</Col>
                  </Row>
                </Col>
                <Col xs={3} sm={3} />
              </Row>
            </div>

            <div className="booking-users--content">
              {isAdmin && (
                <div className="booking-users--total">
                 <TeamOutlined style={{ fontSize: 20, marginRight: 4 }} /> <span style={{ fontSize: 16, position: 'relative', top: 1 }}>{users.length}</span>
                </div>
              )}
              {!isAdmin && (
                <div className="booking-users--total">
                 <b>Your information</b>
                </div>
              )}

              {users.map(this.renderUserRow)}
            </div>

            {isAdmin && (
              <div className="booking-users--footer">
                <Button
                  icon={<PlusOutlined />}
                  disabled={users.length === maxClients}
                  onClick={this.addUser}
                >Client</Button>
              </div>
            )}
          </Layout.Content>

          <Layout.Footer className="fave-layout--footer">
            <div className="fave-layout--footer-actions">
              {isAdmin && (
                <Button
                  size="large"
                  className="btn--add-user"
                  icon={<PlusOutlined />}
                  disabled={users.length === maxClients}
                  onClick={this.addUser}
                >Client</Button>
              )}
              {!isAdmin && <span />}
              <Button
                htmlType="submit"
                type="primary"
                size="large"
              >Next</Button>
            </div>
          </Layout.Footer>
        </Layout>
      </Form>
    );
  }
}

const mapStateToProps = state => ({
  salonId: state.salonStore.salonId,
  isAdmin: state.salonStore.isAdmin,
  primaryColor: state.salonStore.primaryColor,
  accessToken: state.salonStore.accessToken,
  users: state.bookingStore.users,
  newUserFlag: state.bookingStore.newUserFlag,
  suggestedUsers: state.bookingStore.suggestedUsers,
  bookingHistory: state.bookingStore.bookingHistory,
});
const mapDispatchToProps = dispatch => ({
  updateStep: (step: number) => dispatch(bookingActions.updateStep(step)),
  storeActiveUser: (userId: number) => dispatch(bookingActions.storeActiveUser(userId)),
  storeNewUserFlag: (userId: number) => dispatch(bookingActions.storeNewUserFlag(userId)),
  updateUsersList: (users: User[]) => dispatch(bookingActions.updateUsers(users)),
  updateSuggestedUsers: suggestedUsers => dispatch(bookingActions.updateSuggestedUsers(suggestedUsers)),
  fetchUsersByPhone: (options) => dispatch(bookingActions.fetchUsersByPhone(options)),
  storeBookingHistory: (options: Record<string, BookingItem[]>) => dispatch(bookingActions.storeBookingHistory(options)),
  fetchBookingHistory: (salonId: number, phoneNumber: string) => dispatch(bookingActions.fetchBookingHistory(salonId, phoneNumber)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BookingUsers);
